// src/layout/customer/CustomerDetails/CustomerDetails.jsx
import { useState, useEffect } from "react";
import useProfileStore from "../../../store/customer/useProfileStore";
import { toast } from "react-hot-toast";

const CustomerDetails = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    customer_id: "",
    name: "",
    email: "",
    phone_number: "",
    whatsapp_number: "",
    gender: "",
  });
  const [isSameAsPhone, setIsSameAsPhone] = useState(false);
  const [attemptedSave, setAttemptedSave] = useState(false);

  const { profile, fetchProfile, updateProfile, isLoading, isUpdating } =
    useProfileStore();

  const capitalizeFirstLetter = (string) => {
    if (!string) return "";
    return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
  };

  useEffect(() => {
    // Always fetch latest profile on mount to avoid stale data
    fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (profile) {
      const initialFormData = {
        customer_id: profile.customer_id || "",
        name: profile.name || "",
        email: profile.email || "",
        phone_number: profile.contact_number || "",
        whatsapp_number: profile.whatsapp_number || "",
        gender: capitalizeFirstLetter(profile.gender) || "",
      };
      setFormData(initialFormData);
      setIsSameAsPhone(
        initialFormData.phone_number &&
        initialFormData.whatsapp_number === initialFormData.phone_number
      );
    }
  }, [profile]);

  // --- MODIFICATION START: Renamed and simplified toggle logic ---
  const handleToggleEdit = () => {
    // This function is for the "Edit" / "Cancel" button
    if (isEditing && profile) {
      // If in edit mode, this is the "Cancel" action: reset form to original profile data
      const originalFormData = {
        customer_id: profile.customer_id || "",
        name: profile.name || "",
        email: profile.email || "",
        phone_number: profile.contact_number || "",
        whatsapp_number: profile.whatsapp_number || "",
        gender: capitalizeFirstLetter(profile.gender) || "",
      };
      setFormData(originalFormData);
      setIsSameAsPhone(
        originalFormData.phone_number &&
        originalFormData.whatsapp_number === originalFormData.phone_number
      );
    }
    setIsEditing(!isEditing);
  };
  // --- MODIFICATION END ---

  const handleInputChange = (key, value) => {
    let processedValue = value;
    if (["phone_number", "whatsapp_number"].includes(key)) {
      processedValue = value.replace(/\D/g, "").slice(0, 10);
    }

    // --- MODIFICATION START: Capitalize Name ---
    if (key === "name" && processedValue.length > 0) {
      processedValue = processedValue.charAt(0).toUpperCase() + processedValue.slice(1);
    }
    // --- MODIFICATION END ---

    const newFormData = { ...formData, [key]: processedValue };
    if (key === "phone_number" && isSameAsPhone) {
      newFormData.whatsapp_number = processedValue;
    }
    if (
      key === "whatsapp_number" &&
      processedValue !== newFormData.phone_number
    ) {
      setIsSameAsPhone(false);
    }
    setFormData(newFormData);
  };

  const handleSameAsPhoneChange = (e) => {
    const checked = e.target.checked;
    setIsSameAsPhone(checked);
    if (checked) {
      setFormData((prev) => ({ ...prev, whatsapp_number: prev.phone_number }));
    }
  };

  const validateForm = () => {
    if (!formData.name?.trim()) {
      toast.error("Name cannot be empty.");
      return false;
    }
    if (!formData.phone_number?.trim()) {
      toast.error("Phone Number is required.");
      return false;
    }
    if (
      formData.phone_number &&
      (formData.phone_number.length !== 10 ||
        !/^\d{10}$/.test(formData.phone_number))
    ) {
      toast.error("Phone Number must be 10 numeric digits.");
      return false;
    }
    if (
      formData.whatsapp_number &&
      formData.whatsapp_number.length > 0 &&
      (formData.whatsapp_number.length !== 10 ||
        !/^\d{10}$/.test(formData.whatsapp_number))
    ) {
      toast.error("WhatsApp Number must be 10 numeric digits if provided.");
      return false;
    }
    return true;
  };

  // --- MODIFICATION START: Simplified handleSubmit ---
  const handleSubmit = async () => {
    // Mark as attempted to save so we can show red borders
    setAttemptedSave(true);

    if (!validateForm()) return;

    const payload = {
      name: formData.name,
      contact_number: formData.phone_number, // Make sure to send it
      whatsapp_number: formData.whatsapp_number || null,
      gender: formData.gender ? formData.gender.toLowerCase() : null,
    };

    const success = await updateProfile(payload);
    if (success) {
      // The store has re-fetched the profile. The useEffect listening to `profile`
      // has already updated the `formData`. Now we just need to exit edit mode.
      setIsEditing(false);
      setAttemptedSave(false);
    }
  };
  // --- MODIFICATION END ---

  const fields = [
    { key: "customer_id", label: "Ducktail ID", editable: false },
    { key: "name", label: "Customer Name", editable: true, required: true },
    { key: "email", label: "Email", editable: false },
    {
      key: "phone_number",
      label: "Phone Number",
      editable: true,
      required: true, // Made mandatory as per request
      type: "tel",
    },
    {
      key: "whatsapp_number",
      label: "WhatsApp Number",
      editable: true,
      isWhatsapp: true,
      type: "tel",
    },
    {
      key: "gender",
      label: "Gender",
      type: "select",
      options: ["", "Male", "Female", "Other"],
      editable: true,
    },
  ];

  if (isLoading && !profile) {
    return (
      <div className="flex justify-center items-center py-6">
        <div className="w-6 h-6 border-2 border-blue-500 border-dashed rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="py-2 text-xs">
      <div className="flex items-center justify-start gap-4 mb-3 border-b border-gray-400 pb-2">
        <h2 className="text-sm font-semibold">My Details</h2>
        <button
          onClick={handleToggleEdit}
          className="px-3 py-1.5 bg-blue-500 text-white rounded-md hover:bg-blue-600 text-xs"
          disabled={isUpdating}
        >
          {isEditing ? (isUpdating ? "Saving..." : "Cancel") : "Edit"}
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {fields.map(
          ({
            key,
            label,
            type,
            options,
            editable = true,
            required = false,
            isWhatsapp = false,
          }) => {
            const isPhoneField =
              key === "phone_number" || key === "whatsapp_number";

            // Check if field is invalid (empty and required) and user attempted save
            const isInvalid = attemptedSave && required && (!formData[key] || formData[key].toString().trim() === "");

            return (
              <div
                key={key}
                className={`p-1 ${isWhatsapp ? "md:col-span-2" : ""}`}
              >
                <label className="block text-xs font-medium text-gray-600">
                  {label}{" "}
                  {required && isEditing && (
                    <span className="text-red-500">*</span>
                  )}
                </label>
                {type === "select" ? (
                  <select
                    value={formData[key] || ""}
                    onChange={(e) => handleInputChange(key, e.target.value)}
                    disabled={!editable || !isEditing || isUpdating}
                    className={`w-full p-2 mt-1 rounded-md text-xs ${!editable || !isEditing
                      ? "border-none bg-gray-50 cursor-default"
                      : `border ${isInvalid ? "border-red-500" : "border-gray-300"} bg-white`
                      }`}
                  >
                    {options.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt || "-- Select --"}
                      </option>
                    ))}
                  </select>
                ) : (
                  <div className="relative mt-1">
                    {isEditing && isPhoneField && (
                      <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-sm text-gray-500">
                        +91
                      </span>
                    )}
                    <input
                      type={type === "tel" ? "tel" : "text"}
                      inputMode={isPhoneField ? "numeric" : "text"}
                      value={
                        !isEditing && isPhoneField && formData[key]
                          ? `+91 ${formData[key]}`
                          : formData[key] || ""
                      }
                      onChange={(e) => handleInputChange(key, e.target.value)}
                      disabled={
                        !editable ||
                        !isEditing ||
                        isUpdating ||
                        (isWhatsapp && isSameAsPhone && isEditing)
                      }
                      className={`w-full p-2 rounded-md text-xs ${isEditing && isPhoneField ? "pl-10" : ""
                        } ${!editable
                          ? "bg-gray-100 cursor-not-allowed"
                          : isEditing && (!isWhatsapp || !isSameAsPhone)
                            ? `border ${isInvalid ? "border-red-500" : "border-gray-300"} bg-white`
                            : "border-none bg-gray-50 cursor-default"
                        } ${isWhatsapp && isSameAsPhone && isEditing
                          ? "bg-gray-200"
                          : ""
                        }`}
                    />
                  </div>
                )}
                {isWhatsapp && isEditing && (
                  <div className="mt-1.5 flex items-center">
                    <input
                      type="checkbox"
                      id="sameAsPhone"
                      checked={isSameAsPhone}
                      onChange={handleSameAsPhoneChange}
                      className="h-3.5 w-3.5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      disabled={isUpdating}
                    />
                    <label
                      htmlFor="sameAsPhone"
                      className="ml-1.5 text-[10px] text-gray-600"
                    >
                      Same as Phone Number
                    </label>
                  </div>
                )}
              </div>
            );
          }
        )}
      </div>
      {isEditing && (
        <div className="flex justify-end mt-3 p-2">
          <button
            className="px-3 py-1.5 bg-blue-500 text-white rounded-md hover:bg-blue-600 text-xs"
            onClick={handleSubmit}
            disabled={isUpdating}
          >
            {isUpdating ? "Saving..." : "Save Changes"}
          </button>
        </div>
      )}
    </div>
  );
};

export default CustomerDetails;
